import { useCallback } from "react";
import type { NavigateOptions, To } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * A hook that combines useNavigate and useSearchParams to preserve query parameters during navigation.
 * @returns A function that navigates to a new path while preserving all current query parameters
 */
export const useNavigateWithSearchParams = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(
    (to: To, options?: NavigateOptions) => {
      const queryString = searchParams.toString();
      // `to` may be a plain string path or a Partial<Path> object; only the
      // string form can be template-interpolated directly (an object would
      // stringify as "[object Object]").
      const newPath: To =
        typeof to === "string"
          ? queryString
            ? `${to}?${queryString}`
            : to
          : { ...to, search: queryString || to.search };
      if (options) {
        navigate(newPath, options);
      } else {
        navigate(newPath);
      }
    },
    [navigate, searchParams]
  );
};
